import sortBy from 'lodash/sortBy';

import { getRegions } from '@luxuryescapes/lib-regions';

import { CommissionOption, REGION_SORT_ORDER, RuleConditions } from '~/consts/agentHub';

export type CommissionFlightCarrierRulePayload = { code: string; name: string };

export function buildRuleParam(rules: RuleConditions): Record<string, unknown> {
  return Object.entries(rules).reduce<Record<string, unknown>>((acc, [key, conditions]) => {
    if (conditions.length <= 0) {
      return acc;
    }
    if (key === 'flightCarrier') {
      return {
        ...acc,
        flightCarrier: conditions.map(
          (condition): CommissionFlightCarrierRulePayload => ({
            code: String(condition.value),
            name: String(condition.label),
          }),
        ),
      };
    }
    if (key === 'departure') {
      return { ...acc, flightDeparture: conditions.map((condition) => condition.value) };
    }
    if (key === 'arrival') {
      return { ...acc, flightArrival: conditions.map((condition) => condition.value) };
    }
    return { ...acc, [key]: conditions.map((condition) => condition.value) };
  }, {});
}

/** Maps typeahead selections into `additionalInfo.departure` / `additionalInfo.arrival` (city/country shape). */
export function additionalLocationToCommissionOption(loc?: { city?: string; country?: string }): CommissionOption {
  if (!loc) {
    return [];
  }
  const label = [loc.city, loc.country].filter(Boolean).join(', ');
  if (!label) {
    return [];
  }
  return [{ value: label, label }];
}

export function buildAdditionalInfoFromLocationConditions(
  departure: CommissionOption,
  arrival: CommissionOption,
): { departure?: { city?: string; country?: string }; arrival?: { city?: string; country?: string } } | undefined {
  const toLocation = (option: CommissionOption): { city?: string; country?: string } | undefined => {
    if (option.length === 0) {
      return undefined;
    }
    const text = option.map((o) => String(o.label ?? o.value)).join(', ');
    return text ? { city: text } : undefined;
  };
  const dep = toLocation(departure);
  const arr = toLocation(arrival);
  if (!dep && !arr) {
    return undefined;
  }
  return {
    ...(dep ? { departure: dep } : {}),
    ...(arr ? { arrival: arr } : {}),
  };
}

/** Merges departure/arrival into existing `additionalInfo` without dropping other keys (e.g. cabin class). */
export function mergeCommissionAdditionalInfo(
  existing: Record<string, unknown> | undefined,
  departure: CommissionOption,
  arrival: CommissionOption,
): Record<string, unknown> | undefined {
  const fromForm = buildAdditionalInfoFromLocationConditions(departure, arrival);
  const next: Record<string, unknown> = { ...(existing ?? {}) };
  if (fromForm?.departure) {
    next.departure = fromForm.departure;
  } else {
    delete next.departure;
  }
  if (fromForm?.arrival) {
    next.arrival = fromForm.arrival;
  } else {
    delete next.arrival;
  }
  if (Object.keys(next).length === 0) {
    return undefined;
  }
  return next;
}

export const groupByValue = (acc: Map<string | number, string>, curr: { value: string; label: string }) => {
  acc.set(curr.value, curr.label);
  return acc;
};

export const getSortedRegions = () =>
  sortBy(
    getRegions(),
    (region) => {
      const regionOrder = REGION_SORT_ORDER.indexOf(region.name);
      if (regionOrder === -1) {
        // Put it at the bottom of the list if it's not in the desired order
        return 999;
      }
      return regionOrder;
    },
    'asc',
  );

export function renderConditionText(condition: keyof RuleConditions, value: CommissionOption) {
  if (typeof value === 'string') {
    return value;
  }
  switch (condition) {
    case 'agency':
    case 'agent':
    case 'offer':
    case 'vendor':
    case 'location':
    case 'departure':
    case 'arrival':
    case 'productType':
    case 'flightCarrier':
    case 'bundledWithTypes':
    case 'tier':
    case 'dateRange':
    case 'flightClass':
    case 'bundledWithOffer':
    case 'tags':
      return value.map((item) => item.label).join(', ');
    case 'margin':
      return `Min: ${value[0].value}%, Max: ${value[1].value}%`;
    case 'bundleTotalPrice':
      return `Min: ${value[0].value}, Max: ${value[1].value}`;
    case 'minTotalSpend':
      return `Min: ${value[0].value}`;
    default:
      return '';
  }
}
